// src/pages/AttemptPlayer.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';

const authHeader = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` });

function fmtHMS(totalSec){
    const s = Math.max(0, Math.floor(totalSec));
    const h = Math.floor(s/3600);
    const m = Math.floor((s%3600)/60);
    const sec = s%60;
    return (h>0 ? `${h}:` : '') + `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
}

function flagsWithGlobalSec(flags, recClientStartMs){
    const base = Number(recClientStartMs || 0);
    return (flags||[])
        .map(f => {
            const gs = Math.max(0, Math.round(((Number(f.clientTs)||0) - base)/1000));
            return { ...f, globalSec: gs };
        })
        .sort((a,b)=>a.globalSec-b.globalSec);
}

function makeAvailableIdxSet(files){
    const s = new Set();
    for (const f of files||[]) if (Number.isFinite(f.idx)) s.add(Number(f.idx));
    return s;
}

function flagIcon(type){
    switch(String(type||'')){
        case 'face_missing': return '👤✖';
        case 'multi_face':   return '👥';
        case 'alt_tab':      return '↔';
        case 'mic_muted':    return '🔇';
        case 'noise':        return '🎚';
        default:             return '⚑';
    }
}

export default function AttemptPlayer(){
    const { id: attemptId } = useParams();
    const videoRef = useRef(null);
    const prefetchRef = useRef(null);

    const onLoadedRef = useRef(null);
    const onEndedRef  = useRef(null);
    const onErrorRef  = useRef(null);

    const [list, setList] = useState({ files: [], chunkSeconds: 30, gaps: [], lastIdx: -1 });
    const [flagsState, setFlagsState] = useState({ flags: [], rec_client_start_ms: null });
    const [err, setErr] = useState('');
    const [activeFlagIdx, setActiveFlagIdx] = useState(-1);

    const [segIdx, setSegIdx] = useState(0);
    const [localSeek, setLocalSeek] = useState(0);

    // NEW: UI stan — natychmiastowa pozycja po kliku (zanim załaduje się segment)
    const [pendingSeekGlobal, setPendingSeekGlobal] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);

    const fetchList = useCallback(async ()=>{
        const res = await fetch(`/api/rec/attempts/${attemptId}/list`, { credentials:'include', headers: authHeader(), cache:'no-store' });
        const data = await res.json();
        if (!res.ok || !data?.ok) throw new Error(data?.blad || 'Błąd listy segmentów');
        return data;
    }, [attemptId]);

    const fetchFlags = useCallback(async ()=>{
        const rf = await fetch(`/api/exam/attempts/${attemptId}/flags`, { credentials:'include', headers: authHeader(), cache:'no-store' });
        const df = await rf.json().catch(()=>({ flags:[], rec_client_start_ms:null }));
        return {
            flags: Array.isArray(df?.flags) ? df.flags : [],
            rec_client_start_ms: df?.rec_client_start_ms ?? null
        };
    }, [attemptId]);

    useEffect(()=>{
        let cancelled = false;
        (async ()=>{
            try{
                setErr('');
                const [data, fl] = await Promise.all([fetchList(), fetchFlags()]);
                if (cancelled) return;

                setList({
                    files: Array.isArray(data.files) ? data.files : [],
                    chunkSeconds: Number(data.chunkSeconds)||30,
                    gaps: Array.isArray(data.gaps) ? data.gaps : [],
                    lastIdx: Number.isFinite(data.lastIdx) ? data.lastIdx : -1
                });
                setFlagsState(fl);

                const firstIdx = (data.files?.[0]?.idx ?? 0);
                setSegIdx(firstIdx);
                setLocalSeek(0);
                setPendingSeekGlobal(null);
                setIsPlaying(false);
            }catch(e){
                if (!cancelled) setErr(e.message || 'Błąd odczytu danych');
            }
        })();
        return ()=>{ cancelled=true; };
    }, [attemptId, fetchList, fetchFlags]);

    const availableIdx = useMemo(()=>makeAvailableIdxSet(list.files), [list.files]);
    const totalSec = useMemo(()=> (list.lastIdx >= 0 ? (list.lastIdx+1)*list.chunkSeconds : 0), [list.lastIdx, list.chunkSeconds]);

    const gapsInSec = useMemo(()=>{
        const out = [];
        for (const [a,b] of list.gaps||[]){
            if (!Number.isFinite(a) || !Number.isFinite(b) || b<a) continue;
            const start = a * list.chunkSeconds;
            const end   = (b+1) * list.chunkSeconds;
            out.push([start, end]);
        }
        return out;
    }, [list.gaps, list.chunkSeconds]);

    const flags = useMemo(()=>flagsWithGlobalSec(flagsState.flags, flagsState.rec_client_start_ms), [flagsState]);

    const nextAvailableSeg = useCallback((k)=>{
        let s = Math.max(0, Math.floor(k));
        while (s <= list.lastIdx && !availableIdx.has(s)) s++;
        return (s <= list.lastIdx && availableIdx.has(s)) ? s : null;
    }, [availableIdx, list.lastIdx]);

    function inGapSec(sec){
        for (const [a,b] of gapsInSec) if (sec >= a && sec < b) return true;
        return false;
    }
    function snapGlobalSec(t){
        const clamped = Math.max(0, Math.min(t, totalSec));
        if (!inGapSec(clamped)) return clamped;
        for (const [a,b] of gapsInSec) if (clamped >= a && clamped < b) return Math.min(b, totalSec);
        return clamped;
    }

    const refreshUrlForIdx = useCallback(async (idx) => {
        try{
            const data = await fetchList();
            setList(prev => ({
                ...prev,
                files: Array.isArray(data.files) ? data.files : prev.files,
                gaps: Array.isArray(data.gaps) ? data.gaps : prev.gaps,
                lastIdx: Number.isFinite(data.lastIdx) ? data.lastIdx : prev.lastIdx,
                chunkSeconds: Number(data.chunkSeconds)||prev.chunkSeconds
            }));
            const fresh = (data.files||[]).find(f => Number(f.idx) === Number(idx));
            return fresh?.url || null;
        }catch{
            return null;
        }
    }, [fetchList]);

    const playSegmentAt = useCallback(async (seg, offsetSec, retryOnce=true)=>{
        const v = videoRef.current;
        if (!v) return;

        // zdejmij stare listenery
        if (onLoadedRef.current)  v.removeEventListener('loadedmetadata', onLoadedRef.current);
        if (onEndedRef.current)   v.removeEventListener('ended', onEndedRef.current);
        if (onErrorRef.current)   v.removeEventListener('error', onErrorRef.current);
        onLoadedRef.current = null;
        onEndedRef.current  = null;
        onErrorRef.current  = null;

        const file = (list.files || []).find(f => Number(f.idx) === Number(seg));
        if (!file) return;

        const onLoaded = async () => {
            // loaded → potwierdzamy realny start i kasujemy tymczasowy wskaźnik
            setPendingSeekGlobal(null);
            try { v.currentTime = Math.max(0, offsetSec || 0); } catch {}
            try { await v.play(); setIsPlaying(true); } catch { setIsPlaying(false); }
        };
        onLoadedRef.current = onLoaded;

        const onEnded = () => {
            const next = nextAvailableSeg(seg+1);
            if (next != null) {
                setSegIdx(next);
                setLocalSeek(0);
                // ustal natychmiastowy wskaźnik na początek kolejnego segmentu
                setPendingSeekGlobal(next * list.chunkSeconds);
            } else {
                setIsPlaying(false);
            }
        };
        onEndedRef.current = onEnded;

        const onError = async () => {
            if (!retryOnce) {
                const next = nextAvailableSeg(seg+1);
                if (next != null) {
                    setSegIdx(next);
                    setLocalSeek(0);
                    setPendingSeekGlobal(next * list.chunkSeconds);
                }
                return;
            }
            const freshUrl = await refreshUrlForIdx(seg);
            if (freshUrl) {
                v.removeEventListener('loadedmetadata', onLoadedRef.current);
                v.removeEventListener('ended', onEndedRef.current);
                v.removeEventListener('error', onErrorRef.current);

                onLoadedRef.current = onLoaded;
                onEndedRef.current  = onEnded;
                onErrorRef.current  = null;

                v.addEventListener('loadedmetadata', onLoadedRef.current, { once:true });
                v.addEventListener('ended', onEndedRef.current);
                v.crossOrigin = 'anonymous';
                v.preload = 'auto';
                v.src = freshUrl;
                v.load();
                return;
            }
            const next = nextAvailableSeg(seg+1);
            if (next != null) {
                setSegIdx(next);
                setLocalSeek(0);
                setPendingSeekGlobal(next * list.chunkSeconds);
            }
        };
        onErrorRef.current = onError;

        v.addEventListener('loadedmetadata', onLoadedRef.current, { once:true });
        v.addEventListener('ended', onEndedRef.current);
        v.addEventListener('error', onErrorRef.current, { once:true });

        v.crossOrigin = 'anonymous';
        v.preload = 'auto';
        v.src = file.url;
        v.load();

        // natychmiast pokaż kursor na właściwej pozycji (UX: brak „zastoju” na początku segmentu)
        setPendingSeekGlobal(seg * list.chunkSeconds + (offsetSec||0));

        // Prefetch next
        const n2 = nextAvailableSeg(seg+1);
        if (n2 != null) {
            try{
                if (!prefetchRef.current) prefetchRef.current = document.createElement('video');
                const pv = prefetchRef.current;
                const f2 = list.files.find(f=>Number(f.idx)===Number(n2));
                if (f2) { pv.crossOrigin='anonymous'; pv.preload='auto'; pv.src = f2.url; pv.load(); }
            }catch{}
        }
    }, [list.files, list.chunkSeconds, nextAvailableSeg, refreshUrlForIdx]);

    const seekGlobal = useCallback((globalSec)=>{
        const clamped = Math.max(0, Math.min(globalSec, totalSec));
        let t = clamped;
        for (const [a,b] of gapsInSec){ if (t>=a && t<b) { t=b; break; } }
        const seg = Math.floor(t / list.chunkSeconds);
        const local = t - (seg * list.chunkSeconds);
        const targetSeg = availableIdx.has(seg) ? seg : nextAvailableSeg(seg);
        if (targetSeg == null) return;
        setSegIdx(targetSeg);
        setLocalSeek(availableIdx.has(seg) ? local : 0);
        // natychmiast przesuń wskaźnik (aż do loadedmetadata)
        setPendingSeekGlobal(targetSeg * list.chunkSeconds + (availableIdx.has(seg) ? local : 0));
    }, [totalSec, gapsInSec, list.chunkSeconds, availableIdx, nextAvailableSeg]);

    useEffect(()=>{
        if (segIdx == null) return;
        playSegmentAt(segIdx, localSeek, true);
    }, [segIdx, localSeek, playSegmentAt]);

    const globalNow = useCallback(()=>{
        const v = videoRef.current;
        if (!v) return 0;
        const cur = Number.isFinite(v.currentTime) ? v.currentTime : 0;
        return segIdx * list.chunkSeconds + cur;
    }, [segIdx, list.chunkSeconds]);

    // NEW: płynne odświeżanie czasu/cofania paska (co 200 ms)
    const [uiNow, setUiNow] = useState(0);
    useEffect(()=>{
        let t = null;
        const loop = ()=>{
            setUiNow(prev => {
                // jeśli mamy pending (kliknięty cel) – pokazuj jego wartość; inaczej bieżący globalNow
                return pendingSeekGlobal != null ? pendingSeekGlobal : globalNow();
            });
            t = setTimeout(loop, 200);
        };
        loop();
        return ()=>{ if (t) clearTimeout(t); };
    }, [globalNow, pendingSeekGlobal]);

    useEffect(()=>{
        if (!flags.length) { setActiveFlagIdx(-1); return; }
        let t = null;
        const tick = ()=>{
            const g = pendingSeekGlobal != null ? pendingSeekGlobal : globalNow();
            let idx = -1;
            for (let i=0;i<flags.length;i++){
                if (flags[i].globalSec <= g+1.5) idx = i; else break;
            }
            setActiveFlagIdx(idx);
            t = setTimeout(tick, 250);
        };
        tick();
        return ()=>{ if (t) clearTimeout(t); };
    }, [flags, globalNow, pendingSeekGlobal]);

    const jumpToFlag = useCallback((flagIdx)=>{
        if (flagIdx < 0 || flagIdx >= flags.length) return;
        const t = Math.max(0, flags[flagIdx].globalSec - 3);
        seekGlobal(t);
        setActiveFlagIdx(flagIdx);
    }, [flags, seekGlobal]);

    const goPrevFlag = useCallback(()=>{
        if (!flags.length) return;
        const k = activeFlagIdx >= 0 ? activeFlagIdx-1 : flags.length-1;
        jumpToFlag(Math.max(0, k));
    }, [flags.length, activeFlagIdx, jumpToFlag]);

    const goNextFlag = useCallback(()=>{
        if (!flags.length) return;
        const k = activeFlagIdx >= 0 ? activeFlagIdx+1 : 0;
        jumpToFlag(Math.min(flags.length-1, k));
    }, [flags.length, activeFlagIdx, jumpToFlag]);

    const progressPct = useMemo(()=>{
        return totalSec > 0 ? Math.min(100, (uiNow/totalSec)*100) : 0;
    }, [uiNow, totalSec]);

    const markers = useMemo(()=>{
        return flags.map((f, i)=>({
            i,
            leftPct: totalSec>0 ? (Math.min(f.globalSec, totalSec)/totalSec)*100 : 0,
            icon: flagIcon(f.type),
            label: f.type || 'flaga',
            time: f.globalSec
        }));
    }, [flags, totalSec]);

    const gapsPct = useMemo(()=>{
        return gapsInSec.map(([a,b])=>{
            const left = totalSec>0 ? (a/totalSec)*100 : 0;
            const width = totalSec>0 ? ((b-a)/totalSec)*100 : 0;
            return { leftPct:left, widthPct: width };
        });
    }, [gapsInSec, totalSec]);

    function onBarClick(e){
        const rect = e.currentTarget.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const t = x * totalSec;
        seekGlobal(t);
    }

    // Play/Pause
    const togglePlay = async ()=>{
        const v = videoRef.current;
        if (!v) return;
        if (isPlaying) {
            v.pause();
            setIsPlaying(false);
        } else {
            try { await v.play(); setIsPlaying(true); } catch { setIsPlaying(false); }
        }
    };

    if (!list.files.length) return <div style={{maxWidth:1100,margin:'24px auto'}}>Brak nagrań.</div>;

    return (
        <div style={{ maxWidth: 1200, margin:'24px auto', display:'grid', gridTemplateColumns:'1fr 320px', gap:16, marginTop:150 }}>
            <div>
                {/* OŚ CZASU + sterowanie */}
                <div style={{ marginBottom:12, display:'grid', gap:8 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <button  className={'but-smal'}  onClick={togglePlay}>{isPlaying ? '⏸ Pauza' : '▶︎ Odtwarzaj'}</button>
                        <button className={'but-smal'} onClick={goPrevFlag} disabled={!flags.length || activeFlagIdx<=0}>← Poprzednia flaga</button>
                        <button className={'but-smal'} onClick={goNextFlag} disabled={!flags.length || activeFlagIdx>=flags.length-1}>Następna flaga →</button>
                        <div style={{ marginLeft:'auto', fontSize:14 }}>
                            <b>{fmtHMS(uiNow)}</b> / {fmtHMS(totalSec)}
                        </div>
                    </div>

                    <div
                        role="slider"
                        aria-valuemin={0}
                        aria-valuemax={totalSec}
                        aria-valuenow={uiNow}
                        onClick={onBarClick}
                        style={{
                            position:'relative', height:26, borderRadius:13, background:'#f0f2f5',
                            boxShadow:'inset 0 0 0 1px rgba(0,0,0,0.06)', cursor:'pointer'
                        }}
                        title="Kliknij, aby przewinąć"
                    >
                        {/* Gaps */}
                        {gapsPct.map((g,idx)=>(
                            <div key={idx}
                                 title="Brak nagrania"
                                 style={{
                                     position:'absolute', left:`${g.leftPct}%`, width:`${g.widthPct}%`, top:0, bottom:0,
                                     background:'repeating-linear-gradient(45deg,#e9e9ee,#e9e9ee 8px,#dedee6 8px,#dedee6 16px)',
                                     borderRadius:13
                                 }} />
                        ))}
                        {/* Wypełnienie postępu */}
                        <div style={{
                            position:'absolute', left:0, top:0, bottom:0,
                            width: `${progressPct}%`,
                            background:'#d6f5e0',
                            borderRadius:13,
                            transition: pendingSeekGlobal != null ? 'none' : 'width 0.18s linear'
                        }} />
                        {/* Karetka */}
                        <div style={{
                            position:'absolute', top:-3, width:4, bottom:-3, left:`${progressPct}%`,
                            background:'#16a34a', borderRadius:2,
                            transition: pendingSeekGlobal != null ? 'none' : 'left 0.18s linear'
                        }} />
                        {/* Markery flag */}
                        {markers.map(m=>(
                            <button
                                key={m.i}
                                onClick={(e)=>{ e.stopPropagation(); jumpToFlag(m.i); }}
                                title={`${m.label} • ${fmtHMS(m.time)}`}
                                aria-label={`Flaga ${m.label} o ${fmtHMS(m.time)}`}
                                style={{
                                    position:'absolute', top:-6, transform:'translateX(-50%)',
                                    left: `${m.leftPct}%`,
                                    background:'transparent', border:'none', cursor:'pointer', fontSize:14,
                                    filter: activeFlagIdx===m.i ? 'drop-shadow(0 0 2px #16a34a)' : 'none'
                                }}
                            >
                                {m.icon}
                            </button>
                        ))}
                    </div>
                </div>

                {/* WŁASNY ODTWARZACZ (bez natywnego paska) */}
                <video
                    ref={videoRef}
                    playsInline
                    style={{ width:'100%', borderRadius:12, background:'#000' }}
                    preload="auto"
                    crossOrigin="anonymous"
                    controls={false}   // <— wyłączamy natywny pasek, zostaje nasz jeden
                />

                {err && <div style={{ marginTop:10, padding:10, borderRadius:8, background:'#fee', color:'#a00' }}>{err}</div>}
            </div>

            {/* SIDEBAR FLAG */}
            <aside style={{ border:'1px solid #eee', borderRadius:12, padding:12, height:'fit-content' }}>
                <div style={{ fontWeight:700, marginBottom:8 }}>Flagi ({flags.length})</div>
                <div style={{ display:'grid', gap:6, maxHeight: '60vh', overflow:'auto' }}>
                    {flags.map((f, i)=>(
                        <button
                            key={f.id ?? i}
                            onClick={()=>jumpToFlag(i)}
                            aria-current={activeFlagIdx===i ? 'true' : 'false'}
                            style={{
                                display:'grid',
                                gridTemplateColumns:'24px 1fr auto',
                                alignItems:'center',
                                gap:8,
                                padding:'8px 10px',
                                borderRadius:8,
                                border: '1px solid #eee',
                                background: activeFlagIdx===i ? '#f0fff4' : '#fff',
                                textAlign:'left',
                                cursor:'pointer'
                            }}
                            title={`${f.type || 'flaga'} • ${fmtHMS(f.globalSec)}`}
                        >
                            <span style={{ fontSize:16 }}>{flagIcon(f.type)}</span>
                            <span style={{ fontSize:14 }}>{f.type || 'flaga'}</span>
                            <span style={{ fontSize:12, color:'#666' }}>{fmtHMS(f.globalSec)}</span>
                        </button>
                    ))}
                </div>
            </aside>
        </div>
    );
}
